import {
  routePathToRegex,
  shouldReloadForVersionMismatch,
} from "@thalamos/common";

import { config } from "../../config/index.js";
import { RELOAD_SAFE_ROUTE_PATHS } from "../../routing/reloadSafeRoutes.js";

const VERSION_REFRESH_KEY = "haven-version-refresh";

const ALLOWED_PATHS = RELOAD_SAFE_ROUTE_PATHS.map(routePathToRegex);

/**
 * Storage access throws outright in some browsers rather than returning null —
 * private windows, blocked site data. This runs inside an axios response
 * interceptor, so an escaping `DOMException` would fail whichever API call
 * happened to be in flight.
 */
const readRefreshStamp = (): string | null => {
  try {
    return localStorage.getItem(VERSION_REFRESH_KEY);
  } catch {
    return null;
  }
};

/**
 * Returns whether the stamp was actually recorded.
 *
 * The caller must not reload unless it was. The stamp is the only thing that
 * stops a second mismatch triggering a second reload, so reloading without one
 * would loop: every reload would see no stamp, find the same mismatch, and
 * reload again. A stale bundle is a far better outcome than that.
 */
const writeRefreshStamp = (): boolean => {
  try {
    // `Date.now()` is milliseconds since the Unix epoch in UTC, so it does not
    // vary with the browser's timezone. Nothing reads this value back either —
    // only its presence matters — so its format is not a contract.
    localStorage.setItem(VERSION_REFRESH_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

export function checkVersionMismatch(
  serverVersion: string | null | undefined,
): void {
  if (
    !shouldReloadForVersionMismatch(
      serverVersion,
      config.version,
      window.location.pathname,
      ALLOWED_PATHS,
      readRefreshStamp(),
      Date.now(),
    )
  ) {
    return;
  }

  if (!writeRefreshStamp()) {
    return;
  }

  window.location.reload();
}
